import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { produce } from "immer";
import { useExploreValues } from "@/v2/entities/explore/Explore.api";
import { useIntegrations } from "@/api/integration/Integration.api";
import { isIntegrationCompleted } from "@/api/integration/Integration.model";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import {
  ChatSettings,
  LayoutConfiguration,
  PreChatAvatarRadius,
} from "@/components/quack-chat/QuackChatConfig.model";
import { defaultQuackchatHomeLayoutConfiguration } from "../../layout/layout.types";
import type { MultiSelectOption } from "../../../knowledge-hub-channel/knowledge-hub/parts/drawer/MultiSelectDropdown";
import {
  isSupportTicketsPresetTab,
  type SupportTicketsTab,
} from "../constants/supportTicketsTabs";

type PreChatDraft = {
  enabled: boolean;
  welcomeMessage: string;
  welcomeSubtitle: string;
  caseTitle: string;
  avatarSVG: string;
  avatarRadius: PreChatAvatarRadius;
};

type PreChatSource = Pick<
  ChatSettings,
  | "enablePreChatFlow"
  | "preChatWelcomeMessage"
  | "preChatWelcomeSubtitle"
  | "preChatCaseTitle"
  | "preChatAvatarSVG"
  | "preChatAvatarRadius"
>;

function buildPreChatDraft(chatSettings: PreChatSource): PreChatDraft {
  return {
    enabled: chatSettings.enablePreChatFlow ?? false,
    welcomeMessage: chatSettings.preChatWelcomeMessage ?? "",
    welcomeSubtitle: chatSettings.preChatWelcomeSubtitle ?? "",
    caseTitle: chatSettings.preChatCaseTitle ?? "",
    avatarSVG: chatSettings.preChatAvatarSVG ?? "",
    avatarRadius: chatSettings.preChatAvatarRadius ?? "regular",
  };
}

const emptyToUndefined = (value: string) =>
  value.trim() === "" ? undefined : value;

export function useQuackchatLayoutState(simulatorId: string | undefined) {
  const {
    data: configuration,
    isLoading,
    update: saveConfiguration,
    updateStatus,
  } = useQuackchatPlatformConfiguration(simulatorId);

  const { data: exploreValues } = useExploreValues({ source: "tickets" });
  const { data: integrations } = useIntegrations();

  const statusOptions = useMemo<MultiSelectOption[]>(() => {
    return (
      exploreValues?.status?.map((status) => ({
        label: status,
        value: status,
      })) ?? []
    );
  }, [exploreValues]);

  const isSalesforceTicketSystemConnected = useMemo(
    () =>
      integrations?.some(
        (integration) =>
          integration.name === "salesforce" &&
          integration.type === "TICKET_SYSTEM" &&
          isIntegrationCompleted(integration)
      ) || false,
    [integrations]
  );

  const [draftConfiguration, setDraftConfiguration] =
    useState<LayoutConfiguration | null>(null);
  const [lastSavedConfiguration, setLastSavedConfiguration] =
    useState<LayoutConfiguration | null>(null);

  const [preChatDraft, setPreChatDraft] = useState<PreChatDraft | null>(null);
  const [lastSavedPreChat, setLastSavedPreChat] =
    useState<PreChatDraft | null>(null);

  const layout = useMemo(
    () => draftConfiguration ?? defaultQuackchatHomeLayoutConfiguration,
    [draftConfiguration]
  );

  const isDirty = useMemo(() => {
    if (draftConfiguration == null || lastSavedConfiguration == null) {
      return false;
    }

    const layoutDirty =
      JSON.stringify(draftConfiguration) !==
      JSON.stringify(lastSavedConfiguration);

    const preChatDirty =
      JSON.stringify(preChatDraft) !== JSON.stringify(lastSavedPreChat);

    return layoutDirty || preChatDirty;
  }, [
    draftConfiguration,
    lastSavedConfiguration,
    preChatDraft,
    lastSavedPreChat,
  ]);

  useEffect(() => {
    if (configuration == null || isDirty) {
      return;
    }

    const resolvedLayout =
      configuration.chatSettings.layout ?? defaultQuackchatHomeLayoutConfiguration;
    setDraftConfiguration(resolvedLayout);
    setLastSavedConfiguration(resolvedLayout);

    const resolvedPreChat = buildPreChatDraft(configuration.chatSettings);
    setPreChatDraft(resolvedPreChat);
    setLastSavedPreChat(resolvedPreChat);
  }, [configuration, isDirty]);

  const headerStatus = useMemo(() => {
    if (updateStatus === "saving") {
      return updateStatus;
    }

    if (isDirty) {
      return "idle";
    }

    if (updateStatus === "error") {
      return "idle";
    }

    return updateStatus;
  }, [isDirty, updateStatus]);

  const [lastCustomTicketName, setLastCustomTicketName] = useState("Requests");

  useEffect(() => {
    if (draftConfiguration == null) {
      return;
    }

    const currentValue = draftConfiguration.supportTickets.endUserTicketName;
    if (currentValue && !["Tickets", "Cases"].includes(currentValue)) {
      setLastCustomTicketName(currentValue);
    }
  }, [draftConfiguration?.supportTickets.endUserTicketName]);

  const endUserTicketName = useMemo(
    () => layout.supportTickets.endUserTicketName || "",
    [layout.supportTickets.endUserTicketName]
  );

  const currentTab = useMemo<SupportTicketsTab>(
    () =>
      isSupportTicketsPresetTab(endUserTicketName) ? endUserTicketName : "Custom",
    [endUserTicketName]
  );

  const updateLayoutConfiguration = useCallback(
    (recipe: (draft: LayoutConfiguration) => void) => {
      setDraftConfiguration((current) =>
        current ? produce(current, recipe) : current
      );
    },
    []
  );

  const handleGreetingEnabledChange = useCallback(
    (nextValue: boolean) => {
      updateLayoutConfiguration((draft) => {
        draft.greeting.enabled = nextValue;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleGreetingTitleChange = useCallback(
    (value: string) => {
      updateLayoutConfiguration((draft) => {
        draft.greeting.title = value;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleGreetingFallbackChange = useCallback(
    (value: string) => {
      updateLayoutConfiguration((draft) => {
        draft.greeting.fallbackUserName = value;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleAskAiEnabledChange = useCallback(
    (nextValue: boolean) => {
      updateLayoutConfiguration((draft) => {
        draft.askAiInput.enabled = nextValue;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleSupportTicketsEnabledChange = useCallback(
    (nextValue: boolean) => {
      updateLayoutConfiguration((draft) => {
        draft.supportTickets.enabled = nextValue;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleSupportTicketsStyleChange = useCallback(
    (value: "list" | "carousel") => {
      updateLayoutConfiguration((draft) => {
        draft.supportTickets.style = value;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleSupportTicketsStatusesChange = useCallback(
    (value: string[]) => {
      updateLayoutConfiguration((draft) => {
        draft.supportTickets.statusesToDisplayOnThePage = value;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleSupportTicketsReplyChange = useCallback(
    (nextValue: boolean) => {
      updateLayoutConfiguration((draft) => {
        draft.supportTickets.allowUserReplyToTicket = nextValue;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleEndUserTicketNameTabChange = useCallback(
    (value: SupportTicketsTab) => {
      updateLayoutConfiguration((draft) => {
        if (value === "Custom") {
          draft.supportTickets.endUserTicketName = lastCustomTicketName;
        } else {
          draft.supportTickets.endUserTicketName = value;
        }
      });
    },
    [lastCustomTicketName, updateLayoutConfiguration]
  );

  const handleEndUserTicketNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLastCustomTicketName(value);
      updateLayoutConfiguration((draft) => {
        draft.supportTickets.endUserTicketName = value;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleAskAiPlaceholderChange = useCallback(
    (value: string) => {
      updateLayoutConfiguration((draft) => {
        draft.askAiInput.placeholder = value;
      });
    },
    [updateLayoutConfiguration]
  );

  const handleHomePageEnabledChange = useCallback(
    (nextValue: boolean) => {
      updateLayoutConfiguration((draft) => {
        draft.enabled = nextValue;
      });
    },
    [updateLayoutConfiguration]
  );

  const updatePreChatDraft = useCallback(
    (patch: Partial<PreChatDraft>) => {
      setPreChatDraft((current) =>
        current ? { ...current, ...patch } : current
      );
    },
    []
  );

  const handlePreChatEnabledChange = useCallback(
    (nextValue: boolean) => {
      updatePreChatDraft({ enabled: nextValue });
    },
    [updatePreChatDraft]
  );

  const handlePreChatWelcomeMessageChange = useCallback(
    (value: string) => {
      updatePreChatDraft({ welcomeMessage: value });
    },
    [updatePreChatDraft]
  );

  const handlePreChatWelcomeSubtitleChange = useCallback(
    (value: string) => {
      updatePreChatDraft({ welcomeSubtitle: value });
    },
    [updatePreChatDraft]
  );

  const handlePreChatCaseTitleChange = useCallback(
    (value: string) => {
      updatePreChatDraft({ caseTitle: value });
    },
    [updatePreChatDraft]
  );

  const handlePreChatAvatarChange = useCallback(
    (value: string) => {
      updatePreChatDraft({ avatarSVG: value });
    },
    [updatePreChatDraft]
  );

  const handlePreChatAvatarRadiusChange = useCallback(
    (value: PreChatAvatarRadius) => {
      updatePreChatDraft({ avatarRadius: value });
    },
    [updatePreChatDraft]
  );

  const handleSave = useCallback(async () => {
    if (
      draftConfiguration == null ||
      preChatDraft == null ||
      configuration == null
    ) {
      return;
    }

    try {
      await saveConfiguration({
        ...configuration,
        chatSettings: {
          ...configuration.chatSettings,
          layout: draftConfiguration,
          enablePreChatFlow: preChatDraft.enabled,
          preChatWelcomeMessage: emptyToUndefined(preChatDraft.welcomeMessage),
          preChatWelcomeSubtitle: emptyToUndefined(preChatDraft.welcomeSubtitle),
          preChatCaseTitle: emptyToUndefined(preChatDraft.caseTitle),
          preChatAvatarSVG: emptyToUndefined(preChatDraft.avatarSVG),
          preChatAvatarRadius: preChatDraft.avatarRadius,
        },
      });
      setLastSavedConfiguration(draftConfiguration);
      setLastSavedPreChat(preChatDraft);
    } catch {
      return;
    }
  }, [draftConfiguration, preChatDraft, configuration, saveConfiguration]);

  const handleSaveClick = useCallback(() => {
    void handleSave();
  }, [handleSave]);

  const handleDiscard = useCallback(() => {
    if (lastSavedConfiguration == null || lastSavedPreChat == null) {
      return;
    }

    setDraftConfiguration(lastSavedConfiguration);
    setPreChatDraft(lastSavedPreChat);
  }, [lastSavedConfiguration, lastSavedPreChat]);

  const isGreetingEnabled = layout.greeting.enabled;
  const isAskAiEnabled = layout.askAiInput.enabled;
  const isSupportTicketsEnabled = layout.supportTickets.enabled;

  const isReady = useMemo(
    () =>
      configuration != null &&
      draftConfiguration != null &&
      preChatDraft != null,
    [configuration, draftConfiguration, preChatDraft]
  );

  const preChat = preChatDraft ?? buildPreChatDraft({});

  return {
    configuration,
    isLoading,
    isReady,
    layout,
    preChat,
    isDirty,
    headerStatus,
    statusOptions,
    isSalesforceTicketSystemConnected,
    isGreetingEnabled,
    isAskAiEnabled,
    isSupportTicketsEnabled,
    endUserTicketName,
    currentTab,
    handleHomePageEnabledChange,
    handleGreetingEnabledChange,
    handleGreetingTitleChange,
    handleGreetingFallbackChange,
    handleAskAiEnabledChange,
    handleAskAiPlaceholderChange,
    handleSupportTicketsEnabledChange,
    handleSupportTicketsStyleChange,
    handleSupportTicketsStatusesChange,
    handleSupportTicketsReplyChange,
    handleEndUserTicketNameTabChange,
    handleEndUserTicketNameChange,
    handlePreChatEnabledChange,
    handlePreChatWelcomeMessageChange,
    handlePreChatWelcomeSubtitleChange,
    handlePreChatCaseTitleChange,
    handlePreChatAvatarChange,
    handlePreChatAvatarRadiusChange,
    handleSaveClick,
    handleDiscard,
  };
}
